import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import DevPasswordProtection from './components/DevPasswordProtection';
import Layout from './components/Layout/Layout';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CasesPage from './pages/CasesPage';
import CaseDetailPage from './pages/CaseDetailPage';
import CreateCasePage from './pages/CreateCasePage_Pipeline';
import GuidedLearningPage from './pages/GuidedLearningPage';
import GuidedLearningDashboardPage from './pages/GuidedLearningDashboardPage';
import DialoguesPage from './pages/DialoguesPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import AssignmentsPage from './pages/AssignmentsPage';
import EvaluationPage from './pages/EvaluationPage';
import EvaluationViewPage from './pages/EvaluationViewPage';
import CalibrationPage from './pages/CalibrationPage';
import DialogueReviewPage from './pages/DialogueReviewPage';
import './App.css';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Public Route Component (redirect to home if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/cases"
        element={
          <ProtectedRoute>
            <Layout>
              <CasesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/new"
        element={
          <ProtectedRoute>
            <Layout>
              <CreateCasePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/:caseId"
        element={
          <ProtectedRoute>
            <Layout>
              <CaseDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/guided-learning/session/:sessionId"
        element={
          <ProtectedRoute>
            <Layout>
              <GuidedLearningPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dialogues"
        element={
          <ProtectedRoute>
            <Layout>
              <DialoguesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/guided-learning/:caseId"
        element={
          <ProtectedRoute>
            <Layout>
              <GuidedLearningPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/guided-learning"
        element={
          <ProtectedRoute>
            <Layout>
              <GuidedLearningDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assignments"
        element={
          <ProtectedRoute>
            <AssignmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calibration"
        element={
          <ProtectedRoute>
            <Layout>
              <CalibrationPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evaluation"
        element={
          <ProtectedRoute>
            <Layout>
              <EvaluationViewPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Old evaluation page (ranking/feedback) — hidden from nav, kept at /evaluation/rank */}
      <Route
        path="/evaluation/rank"
        element={
          <ProtectedRoute>
            <Layout>
              <EvaluationPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all route - only for truly invalid paths */}
      <Route
        path="/dialogue-review"
        element={
          <ProtectedRoute>
            <Layout>
              <DialogueReviewPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <DevPasswordProtection>
      <AuthProvider>
        <Router>
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </DevPasswordProtection>
  );
};

export default App;
